import {
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonToolbar,
} from '@ionic/react';
import { diff } from 'deep-object-diff';
import { intersection } from 'lodash';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScratchpadHistory,
  useGetScratchpadHistoryLazyQuery,
} from '../../api/graphql/generated';
import { ScratchpadHistoryDiffSlugMappings } from '../../scratchpad/app-slugs';
import { DateField } from '../ui/date-field';
import './scratchpad.css';

enum CompareTo {
  LATEST = 'latest',
  NEXT = 'next',
}

export interface ScratchpadHistoryCompareProps {
  scratchpadId: string;
  selectedHistoryItemId: string;
  nextHistoryItemId?: string;
}

const ScratchpadHistoryCompare = (
  props: ScratchpadHistoryCompareProps
): ReactElement => {
  const { scratchpadId, selectedHistoryItemId, nextHistoryItemId } = props;
  const { i18n } = useTranslation();

  const [currentCompareTo, setCurrentCompareTo] = useState<CompareTo>(
    CompareTo.LATEST
  );

  const [compareToHistoryId, setCompareToHistoryId] = useState<
    string | undefined
  >();

  const [
    getScratchpadHistoryOriginal,
    { data: originalObj, loading: isLoadingOriginalObj },
  ] = useGetScratchpadHistoryLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [
    getScratchpadHistoryUpdated,
    { data: updatedObj, loading: isLoadingUpdatedObj },
  ] = useGetScratchpadHistoryLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    getScratchpadHistoryOriginal({
      variables: {
        input: {
          id: selectedHistoryItemId,
          scratchpadId,
        },
      },
    });
  }, [selectedHistoryItemId, getScratchpadHistoryOriginal, scratchpadId]);

  useEffect(() => {
    getScratchpadHistoryUpdated({
      variables: {
        input: {
          id: compareToHistoryId,
          scratchpadId,
        },
      },
    });
  }, [compareToHistoryId, getScratchpadHistoryUpdated, scratchpadId]);

  const onCompareToChange = (e: CustomEvent) => {
    if (e.detail.value) {
      const val = e.detail.value as CompareTo;

      setCurrentCompareTo(val);

      switch (val) {
        case CompareTo.LATEST:
          setCompareToHistoryId(undefined);
          break;
        case CompareTo.NEXT:
          setCompareToHistoryId(nextHistoryItemId);
          break;
      }
    }
  };

  const isLoadingDiffData = isLoadingOriginalObj || isLoadingUpdatedObj;
  const originalData = originalObj?.getScratchpadHistory;
  const updatedData = updatedObj?.getScratchpadHistory;

  let diffView: ReactElement | null = null;

  if (!isLoadingDiffData && originalData && updatedData) {
    diffView = (
      <ScratchpadHistoryCompareVersions
        originalObj={originalData}
        updatedObj={updatedData}
      />
    );
  } else {
    diffView = (
      <div className="full-height-width content-center ion-padding">
        <IonSpinner className="ion-align-self-center" />
      </div>
    );
  }

  return (
    <div>
      <div className="comparison-segment-control">
        <IonToolbar>
          <IonSegment value={currentCompareTo} onIonChange={onCompareToChange}>
            <IonSegmentButton value={CompareTo.LATEST}>
              {i18n.t('common:scratchpad_version_comparison_to_latest')}
            </IonSegmentButton>
            <IonSegmentButton value={CompareTo.NEXT}>
              {i18n.t('common:scratchpad_version_comparison_to_next')}
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </div>
      {diffView}
    </div>
  );
};

export interface ScratchpadHistoryCompareVersionsProps {
  originalObj: ScratchpadHistory;
  updatedObj: ScratchpadHistory;
}

const ScratchpadHistoryCompareVersions = (
  props: ScratchpadHistoryCompareVersionsProps
): ReactElement => {
  const { originalObj, updatedObj } = props;
  const { i18n } = useTranslation();

  const appSlug = originalObj.app?.slug;

  const DiffComponent = useMemo(() => {
    if (appSlug) {
      return ScratchpadHistoryDiffSlugMappings[appSlug];
    }
  }, [appSlug]);

  const versionsMatch = useMemo(() => {
    const diffResults = diff(originalObj, updatedObj);
    return intersection(Object.keys(diffResults), [
      'appData',
      'name',
      'description',
      'active',
    ]);
  }, [originalObj, updatedObj]);

  if (versionsMatch.length === 0) {
    return (
      <div className="full-height-width content-center ion-padding">
        <IonLabel color="medium">
          {i18n.t('common:scratchpad_version_comparison_versions_match')}
        </IonLabel>
      </div>
    );
  }

  if (originalObj && updatedObj && DiffComponent) {
    return (
      <div className="scratchpad-comapare-wrapper">
        <div className="scratchpad-comapare-header">
          {i18n.t('common:scratchpad_version_comparison_against_version_from')}{' '}
          <DateField date={updatedObj.updatedAt} />
        </div>
        <ScratchpadDeltaComponent
          originalObj={originalObj}
          updatedObj={updatedObj}
        />
        <DiffComponent originalObj={originalObj} updatedObj={updatedObj} />
      </div>
    );
  }

  return <div>{i18n.t('common:scratchpad_version_comparison_missing')}</div>;
};

interface ScratchpadDeltaProps {
  originalObj: ScratchpadHistory;
  updatedObj: ScratchpadHistory;
}

const ScratchpadDeltaComponent = (
  props: ScratchpadDeltaProps
): ReactElement => {
  const { originalObj, updatedObj } = props;
  const { i18n } = useTranslation();

  const isNameChange = originalObj.name !== updatedObj.name;
  const isDescriptionChange =
    originalObj.description !== updatedObj.description;
  const isActiveChange = originalObj.active !== updatedObj.active;

  if (isNameChange || isDescriptionChange || isActiveChange) {
    return (
      <ChangeSection>
        <ChangeSectionLabel
          label={i18n.t('common:scratchpad_version_info_changed')}
        />
        {isNameChange && (
          <div className="scratchpad-info-change-row">
            <ChangeLabel
              label={`${i18n.t(
                'common:scratchpad_version_info_name_changed'
              )}:`}
            />
            <ChangeHighlighter
              oldVal={originalObj.name}
              newVal={updatedObj.name}
            />
          </div>
        )}
        {isDescriptionChange && (
          <div className="scratchpad-info-change-row">
            <ChangeLabel
              label={`${i18n.t(
                'common:scratchpad_version_info_description_changed'
              )}
              :`}
            />
            <ChangeHighlighter
              oldVal={originalObj.description}
              newVal={updatedObj.description}
            />
          </div>
        )}
        {isActiveChange && (
          <div className="scratchpad-info-change-row">
            <ChangeLabel
              label={`${i18n.t(
                'common:scratchpad_version_info_active_changed'
              )}:`}
            />
            <ChangeHighlighter
              oldVal={originalObj.active}
              newVal={updatedObj.active}
            />
          </div>
        )}
      </ChangeSection>
    );
  }
  return <div />;
};

interface ChangeSectionProps {
  children?: ReactNode;
}

const ChangeSection = (props: ChangeSectionProps): ReactElement => {
  const { children } = props;
  return <div className="scratchpad-info-change-container">{children}</div>;
};

interface ChangeLabelProps {
  label: string;
}

const ChangeSectionLabel = (props: ChangeLabelProps): ReactElement => {
  const { label } = props;
  return <div className="scratchpad-info-change-section-title">{label}</div>;
};

const ChangeLabel = (props: ChangeLabelProps): ReactElement => {
  const { label } = props;
  return <span className="scratchpad-info-change-row-label">{label}</span>;
};

interface ChangeHighlighterProps {
  oldVal?: string | boolean | null;
  newVal?: string | boolean | null;
  noValueLabel?: string;
}

const ChangeHighlighter = (props: ChangeHighlighterProps): ReactElement => {
  const { newVal, oldVal, noValueLabel } = props;
  return (
    <>
      <span className="scratchpad-version-old-data">
        {oldVal ?? noValueLabel}
      </span>
      <span className="item-change-subtle">{`→`}</span>
      <span className="scratchpad-version-new-data">
        {newVal ?? noValueLabel}
      </span>
    </>
  );
};

export {
  ChangeHighlighter,
  ChangeLabel,
  ChangeSection,
  ChangeSectionLabel,
  ScratchpadHistoryCompare,
};
